import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plug, RefreshCw, Boxes, Plus, Download, Stethoscope } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { useActiveTenant } from '../contexts/tenant'

type Cred = {
  provider: string
  active: boolean
  connected: boolean
  expiresAt: string | null
  externalAccountId: string | null
  scopes: string | null
  canAutoRenew: boolean
  updatedAt: string
}
type Product = { id: string; sku: string; name: string }
type Location = { id: string; code: string }
type D0Row = { productId: string; locationId: string; quantity: number }
type OrderRow = {
  externalId: string; number: string | null; customerName: string | null
  total: number | null; status: string | null; date: string | null; channel: string | null
  imported: boolean; localStatus: string | null; wsCode: string | null
}

// status LOCAL (nosso) → rótulo + cor
const LOCAL_STATUS: Record<string, { t: string; c: string }> = {
  pending: { t: 'Pendente', c: 'bg-gray-100 text-gray-600' },
  imported: { t: 'Importado', c: 'bg-blue-100 text-blue-700' },
  awaiting_label: { t: 'Aguard. etiqueta', c: 'bg-amber-100 text-amber-700' },
  reserved: { t: 'Reservado', c: 'bg-blue-100 text-blue-700' },
  picking: { t: 'Em separação', c: 'bg-amber-100 text-amber-700' },
  packed: { t: 'Embalado', c: 'bg-indigo-100 text-indigo-700' },
  shipped: { t: 'Enviado', c: 'bg-green-100 text-green-700' },
  invoiced: { t: 'Faturado', c: 'bg-green-100 text-green-700' },
  divergent: { t: 'Divergente', c: 'bg-red-100 text-red-700' },
  returned: { t: 'Devolvido', c: 'bg-red-100 text-red-700' },
  cancelled: { t: 'Cancelado', c: 'bg-red-100 text-red-700' },
}

const PROVIDERS = [
  { value: 'bling', label: 'Bling', isErp: true },
  { value: 'olist', label: 'Olist', isErp: true },
  { value: 'melhorenvio', label: 'Melhor Envio', isErp: false },
] as const

const providerLabel = (p: string) => PROVIDERS.find(x => x.value === p)?.label ?? p

export function IntegrationsPage() {
  const tenantId = useActiveTenant()
  const qc = useQueryClient()

  const [tab, setTab] = useState<'operacao' | 'canais' | 'conexoes'>('operacao')
  const [connectModal, setConnectModal] = useState(false)
  const [form, setForm] = useState({ provider: 'bling', accessToken: '', refreshToken: '', externalAccountId: '', expiresAt: '' })
  const [syncResult, setSyncResult] = useState<string | null>(null)
  const [d0Rows, setD0Rows] = useState<D0Row[]>([{ productId: '', locationId: '', quantity: 0 }])
  const [d0Result, setD0Result] = useState<string | null>(null)
  const [blingApp, setBlingApp] = useState({ clientId: '', clientSecret: '' })
  const [meApp, setMeApp] = useState({ clientId: '', clientSecret: '' })
  const [doneMsg, setDoneMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [refreshDays, setRefreshDays] = useState(7)
  const [nfSyncYear, setNfSyncYear] = useState(new Date().getFullYear())
  const [nfSyncMonth, setNfSyncMonth] = useState(new Date().getMonth() + 1)
  const [nfSyncMsg, setNfSyncMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [nfList, setNfList] = useState('')
  const [nfAsShipped, setNfAsShipped] = useState(false)
  const [nfResult, setNfResult] = useState<{ ok: boolean; text: string } | null>(null)
  const [nfDebugOut, setNfDebugOut] = useState<string | null>(null)
  const [labelOrderId, setLabelOrderId] = useState('')
  const [labelDebugOut, setLabelDebugOut] = useState<string | null>(null)
  type OrdEnvio = { nf: string | null; serie: string | null; chave: string | null; transportadora: string | null; rastreio: string | null; statusEnvio: string; pedido: string | null; statusPedido: string | null; onda: string[]; destinatario: string | null; uf: string | null; cidade: string | null }
  type OrdRow = { ord: string; encontrada: boolean; envios: OrdEnvio[] }
  const [ordText, setOrdText] = useState('')
  const [ordResult, setOrdResult] = useState<OrdRow[] | null>(null)
  const [ordSoDiverg, setOrdSoDiverg] = useState(false)
  // Divergência: etiqueta ainda não postada (authorized/generated) mas o pedido já
  // consta EXPEDIDO no WsFull → provável "ainda em casa" (marcado enviado sem sair).
  const ordEhDiverg = (e: OrdEnvio) => !['posted', 'delivered', 'canceled'].includes(e.statusEnvio) && ['shipped', 'invoiced'].includes(e.statusPedido ?? '')
  const [ordDeepMsg, setOrdDeepMsg] = useState<string | null>(null)
  const ordDeep = useMutation({
    mutationFn: async () => {
      const ords = ordText.split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean)
      if (!ords.length) throw new Error('Cole ao menos uma ORD')
      const { data: start } = await api.post('/integrations/ord-deep-search', { tenantId, ords })
      if (start?.started === false) throw new Error(start.reason ?? 'Já há uma busca em andamento')
      for (let i = 0; i < 400; i++) { // teto ~20 min acompanhando
        await new Promise(r => setTimeout(r, 3000))
        const { data: st } = await api.get('/integrations/ord-deep-search/status', { params: { tenantId } })
        if (st?.running) { setOrdDeepMsg(`⏳ ${st.msg ?? 'buscando…'}`); continue }
        if (st?.error) throw new Error(st.error)
        return st?.result ?? {}
      }
      throw new Error('Ainda rodando no servidor — reabra em instantes.')
    },
    onSuccess: (r: any) => { setOrdDeepMsg(`Busca concluída — ${r.found ?? 0}/${r.totalWanted ?? 0} ORDs achadas em ${r.pages ?? 0} páginas.`); ordReport.mutate() },
    onError: (e: any) => setOrdDeepMsg(apiErr(e, 'Falha na busca profunda')),
  })
  const [meDbgOut, setMeDbgOut] = useState<string | null>(null)
  const meDbg = useMutation({
    mutationFn: async () => (await api.get('/integrations/me-orders-debug', { params: { tenantId } })).data,
    onSuccess: (r: any) => setMeDbgOut(JSON.stringify(r, null, 2)),
    onError: (e: any) => setMeDbgOut(apiErr(e, 'Falha no diagnóstico do Melhor Envio')),
  })
  const ordReport = useMutation({
    mutationFn: async () => {
      const ords = ordText.split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean)
      return (await api.post('/integrations/ord-report', { tenantId, ords })).data as OrdRow[]
    },
    onSuccess: (r) => setOrdResult(r),
  })
  function exportOrdCsv() {
    if (!ordResult) return
    const head = ['ORD', 'NF', 'Serie', 'UF', 'Cidade', 'Chave', 'Transportadora', 'Rastreio', 'StatusEnvio', 'Pedido', 'StatusPedido', 'Onda', 'Destinatario']
    const esc = (v: unknown) => String(v ?? '').replace(/;/g, ',').replace(/\n/g, ' ')
    const lines = [head.join(';')]
    for (const r of ordResult) {
      if (!r.envios.length) { lines.push([r.ord, 'NAO ENCONTRADA', '', '', '', '', '', '', '', '', '', '', ''].join(';')); continue }
      for (const e of r.envios) lines.push([r.ord, e.nf, e.serie, e.uf, e.cidade, e.chave, e.transportadora, e.rastreio, e.statusEnvio, e.pedido, e.statusPedido, (e.onda ?? []).join(' '), e.destinatario].map(esc).join(';'))
    }
    const url = URL.createObjectURL(new Blob(['﻿' + lines.join('\n')], { type: 'text/csv' }))
    const a = document.createElement('a'); a.href = url; a.download = 'ord-nf.csv'; document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 15000)
  }
  const [backfillFrom, setBackfillFrom] = useState('2026-05-05')
  const [backfilling, setBackfilling] = useState(false)
  const [backfillMsg, setBackfillMsg] = useState<string | null>(null)

  // ── Pedidos do ERP (preview + importar selecionados) ───────────────────────
  const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const [ordProvider, setOrdProvider] = useState<'bling' | 'olist'>('bling')
  const [ordFrom, setOrdFrom] = useState(isoDate(new Date(Date.now() - 7 * 86400000)))
  const [ordTo, setOrdTo] = useState(isoDate(new Date()))
  const [orders, setOrders] = useState<OrderRow[] | null>(null)
  const [ordSel, setOrdSel] = useState<Set<string>>(new Set())
  const [ordMsg, setOrdMsg] = useState<string | null>(null)

  const { data: blingCfg } = useQuery<{ configured: boolean }>({
    queryKey: ['bling-app-config'],
    queryFn: async () => (await api.get('/integrations/bling/app-config')).data,
  })
  const saveBlingApp = useMutation({
    mutationFn: async () => api.post('/integrations/bling/app-config', blingApp),
    onSuccess: () => { setBlingApp({ clientId: '', clientSecret: '' }); qc.invalidateQueries({ queryKey: ['bling-app-config'] }) },
  })

  const { data: meCfg } = useQuery<{ configured: boolean }>({
    queryKey: ['me-app-config'],
    queryFn: async () => (await api.get('/integrations/melhorenvio/app-config')).data,
  })
  const saveMeApp = useMutation({
    mutationFn: async () => api.post('/integrations/melhorenvio/app-config', meApp),
    onSuccess: () => { setMeApp({ clientId: '', clientSecret: '' }); qc.invalidateQueries({ queryKey: ['me-app-config'] }) },
  })

  const { data: reconcile } = useQuery<{ total: number; enviados: number; comNf: number }>({
    queryKey: ['orders-reconcile', tenantId],
    queryFn: async () => (await api.get('/integrations/orders/reconcile', { params: { tenantId } })).data,
    enabled: !!tenantId,
    refetchInterval: backfilling ? 5000 : false,
  })

  const { data: creds = [], isLoading } = useQuery<Cred[]>({
    queryKey: ['integration-credentials', tenantId],
    queryFn: async () => (await api.get('/integrations/credentials', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: async () => (await api.get('/stock/locations')).data,
    enabled: !!tenantId,
  })

  const connect = useMutation({
    mutationFn: async () => api.post('/integrations/credentials', {
      tenantId,
      provider: form.provider,
      accessToken: form.accessToken || undefined,
      refreshToken: form.refreshToken || undefined,
      externalAccountId: form.externalAccountId || undefined,
      expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integration-credentials', tenantId] })
      setConnectModal(false)
      setForm({ provider: 'bling', accessToken: '', refreshToken: '', externalAccountId: '', expiresAt: '' })
    },
  })

  // O Fastify serializa erros como { statusCode, error, message } — o `message`
  // carrega a resposta crua do provedor. Traduz os casos conhecidos.
  const apiErr = (e: any, fallback: string) => {
    const raw: string = e?.response?.data?.message
      ?? e?.response?.data?.detail ?? e?.response?.data?.title ?? e?.message ?? ''
    if (/insufficient_scope/i.test(raw)) {
      return 'O app no Bling não tem permissão de Produtos/Pedidos. Edite os escopos do app no portal do Bling e refaça a autorização (Reconectar).'
    }
    if (/invalid_grant|refresh token|Invalid refresh/i.test(raw)) {
      return 'A autorização anterior expirou ou foi revogada pelo provedor (comum após mexer nos escopos). Clique em Reconectar (OAuth) e conclua a autorização até o fim.'
    }
    if (/invalid_token|401/.test(raw)) {
      return 'Token recusado pelo provedor. Clique em Reconectar (OAuth).'
    }
    if (/TimeoutError/i.test(raw)) {
      return 'O provedor demorou demais para responder. Tente de novo em alguns minutos.'
    }
    return raw || fallback
  }

  const sync = useMutation({
    mutationFn: async (provider: string) =>
      (await api.post('/integrations/sync-products', { tenantId, provider })).data,
    onSuccess: (r: { total: number; created: number; updated: number; skipped: number }) => {
      setSyncResult(`${r.created} criados · ${r.updated} atualizados · ${r.skipped} ignorados (de ${r.total})`)
      qc.invalidateQueries({ queryKey: ['products', tenantId] })
    },
    onError: (e: any) => setSyncResult(apiErr(e, 'Erro no sync')),
  })

  // "Não está subindo pedidos" tinha três causas possíveis e nenhuma aparecia:
  // token recusado pelo ERP, nenhum pedido na situação que o WsFull importa, ou
  // simplesmente não houve venda. O diagnóstico responde qual das três é.
  type DiagImport = {
    provider: string
    tokenOk: boolean | null
    erro: string | null
    regra?: string
    janela?: { dataInicial: string; dias: number }
    pedidosNaJanela?: number
    prontosParaImportar?: number
    porSituacao?: { situacao: string; pedidos: number; entra: boolean }[]
    amostra?: { id: string; numero: string | null; situacao: string; data: string | null; entra: boolean }[]
    credencial?: { ativa: boolean; atualizadaEm: string | null; expiraEm: string | null }
    noWsFull?: { pedidos: number; ultimoEntrouEm: string | null }
    autoSync?: { at: number | null; ok?: boolean; imported?: number; total?: number; error?: string } | null
  }
  const [diag, setDiag] = useState<DiagImport | null>(null)
  const diagnosticar = useMutation({
    mutationFn: async (provider: string) =>
      (await api.get('/integrations/orders/diagnostico', { params: { tenantId, provider } })).data as DiagImport,
    onSuccess: (d) => { setDiag(d); setSyncResult(null) },
    onError: (e: any) => { setDiag(null); setSyncResult(apiErr(e, 'Erro no diagnóstico')) },
  })

  const importOrders = useMutation({
    mutationFn: async (provider: string) =>
      (await api.post('/integrations/orders/import', { tenantId, provider })).data,
    onSuccess: (r: { total: number; imported: number; failed: number }) => {
      setSyncResult(`Pedidos: ${r.imported} importados · ${r.failed} falhas (de ${r.total})`)
      qc.invalidateQueries({ queryKey: ['orders-board', tenantId] })
    },
    onError: (e: any) => setSyncResult(apiErr(e, 'Erro na importação')),
  })

  // Rotina única: importa pedidos do ERP + casa etiquetas do Melhor Envio.
  const refreshAll = useMutation({
    mutationFn: async () => {
      const erp = creds.find(c => c.connected && (c.provider === 'bling' || c.provider === 'olist'))?.provider
      if (!erp) throw new Error('Conecte um ERP (Bling ou Olist) neste cliente primeiro')
      // Dispara em background (import + sync do ME passam de 60s) e acompanha por status.
      const { data: start } = await api.post('/integrations/refresh', { tenantId, provider: erp, days: refreshDays })
      if (start?.started === false) throw new Error(start.reason ?? 'Já há uma carga em andamento')
      for (let i = 0; i < 240; i++) { // ~12 min de teto acompanhando
        await new Promise(r => setTimeout(r, 3000))
        const { data: st } = await api.get('/integrations/refresh/status', { params: { tenantId } })
        if (st?.running) { setDoneMsg({ ok: true, text: `⏳ ${st.msg ?? 'processando…'}` }); continue }
        if (st?.error) throw new Error(st.error)
        return st?.result ?? {}
      }
      throw new Error('Ainda rodando no servidor — reabra em instantes; a carga não para se você sair da tela.')
    },
    onSuccess: (r: any) => {
      const o = r.orders ?? {}, s = r.shipments ?? {}
      const shipTxt = s.error
        ? `etiquetas não sincronizadas (${s.error})`
        : `${(s.total ?? 0) - (s.unmatched ?? 0)} etiqueta(s) casada(s) · ${s.unmatched ?? 0} sem casar`
      setDoneMsg({ ok: true, text: `Concluído — ${o.imported ?? 0} novo(s) · ${o.skipped ?? 0} já existiam · ${o.failed ?? 0} falha(s) (de ${o.total ?? 0}) · ${shipTxt}` })
      qc.invalidateQueries({ queryKey: ['orders-board', tenantId] })
    },
    onError: (e: any) => setDoneMsg({ ok: false, text: apiErr(e, 'Erro na rotina') }),
  })

  // Sync NF-first por mês: importa por NF-e emitida (pega TikTok/ML e notas atrasadas).
  const nfSync = useMutation({
    mutationFn: async () => {
      const erp = creds.find(c => c.connected && (c.provider === 'bling' || c.provider === 'olist'))?.provider
      if (!erp) throw new Error('Conecte um ERP (Bling ou Olist) neste cliente primeiro')
      const pad = (n: number) => String(n).padStart(2, '0')
      const from = `${nfSyncYear}-${pad(nfSyncMonth)}-01`
      const to = `${nfSyncYear}-${pad(nfSyncMonth)}-${pad(new Date(nfSyncYear, nfSyncMonth, 0).getDate())}`
      const { data: start } = await api.post('/integrations/nf-sync', { tenantId, provider: erp, from, to })
      if (start?.started === false) throw new Error(start.reason ?? 'Já há uma sincronização em andamento')
      for (let i = 0; i < 300; i++) { // ~15 min de teto acompanhando
        await new Promise(r => setTimeout(r, 3000))
        const { data: st } = await api.get('/integrations/nf-sync/status', { params: { tenantId } })
        if (st?.running) { setNfSyncMsg({ ok: true, text: `⏳ ${st.msg ?? 'processando…'}` }); continue }
        if (st?.error) throw new Error(st.error)
        return st?.result ?? {}
      }
      throw new Error('Ainda rodando no servidor — reabra em instantes; não para se você sair da tela.')
    },
    onSuccess: (r: any) => {
      setNfSyncMsg({ ok: true, text: `Concluído — ${r.encontradas ?? 0} NF-e no mês · ${r.novas ?? 0} nova(s) · ${r.imported ?? 0} importada(s) · ${r.failed ?? 0} falha(s)` })
      qc.invalidateQueries({ queryKey: ['orders-board', tenantId] })
    },
    onError: (e: any) => setNfSyncMsg({ ok: false, text: apiErr(e, 'Erro na sincronização por NF') }),
  })

  // Reparo das notas que entraram sem data de emissão.
  //
  // O caminho NF-first gravava número e chave e não gravava a emissão. Como todo
  // relatório mensal corta por emission_date, essas notas existiam no banco e
  // sumiam da apuração — foi a diferença entre as 4.376 NF-e que o Bling contava
  // em agosto e as 3.746 que o extrato mostrava. A entrada nova já vem
  // corrigida; isto acerta o que ficou para trás.
  const { data: semEmissao, refetch: recontarEmissao } = useQuery<{
    comNf: number; semEmissao: number; recuperaveis: number; semIdDaNota: number
  }>({
    queryKey: ['emissao-faltante', tenantId],
    queryFn: async () => (await api.get('/integrations/emissao-faltante', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })
  // Contato do destinatário (e-mail/telefone) no passado. O dado sempre veio do
  // ERP dentro do endereço, sem coluna própria — então a maior parte se recupera
  // da própria base, de graça; só o que sobrar vai buscar na NF-e do ERP.
  const { data: semContato, refetch: recontarContato, error: contatoErro } = useQuery<{
    total: number; sem_nada: number; sem_email: number; sem_telefone: number; buscaveis_no_erp: number
  }>({
    queryKey: ['contato-faltante', tenantId],
    queryFn: async () => (await api.get('/integrations/contato-faltante', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })
  const [contatoMsg, setContatoMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const contatoDaBase = useMutation({
    mutationFn: async () => (await api.post('/integrations/contato-faltante/da-base', { tenantId })).data,
    onSuccess: (r: any) => {
      // Zero aqui não é falha: no Bling o payload guardado é a etiqueta de
      // endereço, que não carrega contato. O caminho é a NF-e (botão ao lado).
      setContatoMsg({
        ok: true,
        text: r.preenchidos > 0
          ? `${r.preenchidos.toLocaleString('pt-BR')} pedido(s) preenchidos com o que já estava guardado no próprio pedido.`
          : 'Nenhum: o endereço guardado nestes pedidos (etiqueta do Bling) não traz e-mail nem telefone — '
            + 'esses dados estão na NF-e. Use "Buscar o resto no ERP".',
      })
      recontarContato()
    },
    onError: (e: any) => setContatoMsg({ ok: false, text: apiErr(e, 'Falha ao preencher pela base') }),
  })
  const contatoDoErp = useMutation({
    mutationFn: async () => {
      const erp = creds.find(c => c.connected && (c.provider === 'bling' || c.provider === 'olist'))?.provider
      if (!erp) throw new Error('Conecte um ERP (Bling ou Olist) neste cliente primeiro')
      const { data: start } = await api.post('/integrations/contato-faltante/backfill', { tenantId, provider: erp })
      if (start?.started === false) throw new Error(start.reason ?? 'Já há um backfill em andamento')
      for (let i = 0; i < 400; i++) { // ~20 min: é uma chamada ao ERP por nota
        await new Promise(r => setTimeout(r, 3000))
        const { data: st } = await api.get('/integrations/contato-faltante/status', { params: { tenantId } })
        if (st?.running) { setContatoMsg({ ok: true, text: `⏳ ${st.msg ?? 'processando…'}` }); continue }
        if (st?.error) throw new Error(st.error)
        return st?.result ?? {}
      }
      throw new Error('Ainda rodando no servidor — reabra em instantes; não para se você sair da tela.')
    },
    onSuccess: (r: any) => {
      setContatoMsg({
        ok: true,
        text: `Concluído — ${(r.daBase ?? 0).toLocaleString('pt-BR')} vieram da própria base`
          + ` · ${(r.corrigidas ?? 0).toLocaleString('pt-BR')} buscados na NF do ERP`
          + (r.semDado ? ` · ${r.semDado} sem contato no próprio ERP` : '')
          + (r.falhas ? ` · ${r.falhas} falha(s)` : ''),
      })
      recontarContato()
    },
    onError: (e: any) => setContatoMsg({ ok: false, text: apiErr(e, 'Falha no backfill de contato') }),
  })

  const [reparoMsg, setReparoMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const repararEmissao = useMutation({
    mutationFn: async () => {
      const erp = creds.find(c => c.connected && (c.provider === 'bling' || c.provider === 'olist'))?.provider
      if (!erp) throw new Error('Conecte um ERP (Bling ou Olist) neste cliente primeiro')
      const { data: start } = await api.post('/integrations/emissao-faltante/reparar', { tenantId, provider: erp })
      if (start?.started === false) throw new Error(start.reason ?? 'Já há um reparo em andamento')
      for (let i = 0; i < 400; i++) { // ~20 min: é uma chamada ao ERP por nota
        await new Promise(r => setTimeout(r, 3000))
        const { data: st } = await api.get('/integrations/emissao-faltante/status', { params: { tenantId } })
        if (st?.running) { setReparoMsg({ ok: true, text: `⏳ ${st.msg ?? 'processando…'}` }); continue }
        if (st?.error) throw new Error(st.error)
        return st?.result ?? {}
      }
      throw new Error('Ainda rodando no servidor — reabra em instantes; não para se você sair da tela.')
    },
    onSuccess: (r: any) => {
      setReparoMsg({
        ok: true,
        text: `Concluído — ${r.corrigidas ?? 0} nota(s) com emissão gravada`
          + (r.semData ? ` · ${r.semData} sem data no próprio ERP` : '')
          + (r.falhas ? ` · ${r.falhas} falha(s)` : ''),
      })
      recontarEmissao()
      qc.invalidateQueries({ queryKey: ['orders-board', tenantId] })
    },
    onError: (e: any) => setReparoMsg({ ok: false, text: apiErr(e, 'Erro no reparo de emissão') }),
  })

  // Importa notas específicas pela lista de números de NF.
  const importNfs = useMutation({
    mutationFn: async () => {
      const erp = creds.find(c => c.connected && (c.provider === 'bling' || c.provider === 'olist'))?.provider
      if (!erp) throw new Error('Conecte um ERP (Bling ou Olist) neste cliente primeiro')
      const nfs = nfList.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
      if (!nfs.length) throw new Error('Cole ao menos um número de NF')
      return (await api.post('/integrations/import-nfs', { tenantId, provider: erp, nfs, asShipped: nfAsShipped })).data
    },
    onSuccess: (r: any) => {
      const nf = r.notFound?.length ? ` · não encontradas: ${r.notFound.join(', ')}` : ''
      setNfResult({ ok: (r.notFound?.length ?? 0) === 0, text: `${r.imported ?? 0} importada(s) · ${r.matched ?? 0} casada(s) de ${r.requested ?? 0} pedida(s)${nf}` })
      qc.invalidateQueries({ queryKey: ['orders-board', tenantId] })
    },
    onError: (e: any) => setNfResult({ ok: false, text: apiErr(e, 'Erro ao importar NFs') }),
  })

  // Diagnóstico temporário: mostra a resposta crua do /nfe do Bling p/ a 1ª NF.
  const nfDebug = useMutation({
    mutationFn: async () => {
      const erp = creds.find(c => c.connected && (c.provider === 'bling' || c.provider === 'olist'))?.provider
      if (!erp) throw new Error('Conecte um ERP primeiro')
      const first = nfList.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)[0]
      if (!first) throw new Error('Cole ao menos um número de NF')
      return (await api.get('/integrations/nf-debug', { params: { tenantId, provider: erp, nf: first } })).data
    },
    onSuccess: (r: any) => setNfDebugOut(JSON.stringify(r, null, 2)),
    onError: (e: any) => setNfDebugOut(apiErr(e, 'Erro no diagnóstico')),
  })

  // Fase 0: diagnóstico de etiqueta — pedido cru + tentativas nos endpoints de
  // logística/etiqueta do Bling (descobrir como puxar etiqueta de TikTok/ML).
  const [labelNf, setLabelNf] = useState('')
  const labelDebug = useMutation({
    mutationFn: async () => {
      const nf = labelNf.trim()
      const id = labelOrderId.trim()
      if (!nf && !id) throw new Error('Informe o número da NF ou o id do pedido no Bling')
      const params = nf ? { tenantId, provider: 'bling', nf } : { tenantId, provider: 'bling', orderId: id }
      return (await api.get('/integrations/orders/label-test', { params })).data
    },
    onSuccess: (r: any) => setLabelDebugOut(JSON.stringify(r, null, 2)),
    onError: (e: any) => setLabelDebugOut(apiErr(e, 'Erro no diagnóstico de etiqueta')),
  })

  // Diagnóstico de canais: mostra o que o Bling devolve em /lojas e o canal
  // resolvido dos últimos pedidos — pra entender por que o ícone vem genérico.
  const chanDebug = useMutation({
    mutationFn: async () => {
      const erp = creds.find(c => c.connected && (c.provider === 'bling' || c.provider === 'olist'))?.provider
      if (!erp) throw new Error('Conecte um ERP primeiro')
      const fmt = (d: Date) => d.toISOString().slice(0, 10)
      const to = new Date()
      const from = new Date(to.getTime() - 3 * 86_400_000)
      const r: any = (await api.get('/integrations/orders/preview', { params: { tenantId, provider: erp, from: fmt(from), to: fmt(to) } })).data
      const me: any = await api.get('/integrations/me-debug', { params: { tenantId } }).then(x => x.data).catch(e => ({ erro: apiErr(e, 'me-debug falhou') }))
      return {
        lojas_distintas_para_mapear: r?._debug?.lojas_distintas ?? [],
        lojas_do_bling: r?._debug?.lojasSample ?? r?._debug?.lojasError ?? 'sem _debug',
        loja_no_pedido: r?._debug?.pedidoLoja ?? null,
        pedido_detalhe_loja: r?._debug?.pedidoDetalhe_loja ?? null,
        pedido_detalhe_transporte: r?._debug?.pedidoDetalhe_transporte ?? null,
        pedido_detalhe_notaFiscal: r?._debug?.pedidoDetalhe_notaFiscal ?? null,
        canais_resolvidos: (r?.orders ?? []).slice(0, 12).map((o: any) => ({ ws: o.wsCode, ref: o.number, canal: o.channel })),
        melhor_envio_envio_cru: me,
      }
    },
    onSuccess: (r: any) => setNfDebugOut(JSON.stringify(r, null, 2)),
    onError: (e: any) => setNfDebugOut(apiErr(e, 'Erro no diagnóstico de canais')),
  })

  // Compara as etiquetas "Autorizada" do WsFull com o Melhor Envio.
  const shipReconcile = useMutation({
    mutationFn: async (fix: boolean) =>
      (await api.get('/integrations/shipments-reconcile', { params: { tenantId, ...(fix ? { fix: 1 } : {}) } })).data,
    onSuccess: (r: any) => { setNfDebugOut(JSON.stringify(r, null, 2)); qc.invalidateQueries({ queryKey: ['orders-board', tenantId] }) },
    onError: (e: any) => setNfDebugOut(apiErr(e, 'Erro na conferência de etiquetas')),
  })

  // Backfill do histórico: dispara no SERVIDOR (background, sem timeout de 60s) e
  // acompanha o progresso pelo status. Idempotente — re-rodar pula os já importados.
  async function pollBackfill() {
    try {
      const { data: s } = await api.get('/integrations/backfill/status', { params: { tenantId } })
      const base = `${s.done ?? 0}/${s.total ?? 0} janelas · ${s.novos ?? 0} novos · ${s.existiam ?? 0} já existiam${s.erros ? ` · ${s.erros} erro(s)` : ''}`
      if (s.running) {
        setBackfillMsg(`${base} — ${s.msg ?? ''}`)
        setTimeout(pollBackfill, 2500)
      } else {
        setBackfillMsg(s.total ? `✅ Concluído — ${base}` : (s.msg ?? 'nenhum backfill'))
        setBackfilling(false)
        qc.invalidateQueries({ queryKey: ['orders-board', tenantId] })
      }
    } catch {
      setTimeout(pollBackfill, 3000) // rede instável: continua tentando
    }
  }
  async function runBackfill() {
    const erp = creds.find(c => c.connected && (c.provider === 'bling' || c.provider === 'olist'))?.provider
    if (!erp) { setBackfillMsg('Conecte um ERP (Bling ou Olist) neste cliente primeiro'); return }
    setBackfilling(true); setBackfillMsg('iniciando no servidor…')
    try {
      const { data } = await api.post('/integrations/backfill/start', { tenantId, provider: erp, from: backfillFrom })
      if (data.started === false) setBackfillMsg(data.reason ?? 'Já há um backfill em andamento')
    } catch (e: any) { setBackfillMsg(apiErr(e, 'Erro ao iniciar o backfill')); setBackfilling(false); return }
    pollBackfill()
  }

  const d0 = useMutation({
    mutationFn: async () => (await api.post('/integrations/d0', {
      tenantId,
      counts: d0Rows.filter(r => r.productId && r.locationId && r.quantity > 0),
    })).data,
    onSuccess: (r: { entries: number; totalUnits: number; locations: number }) => {
      setD0Result(`${r.entries} lançamentos · ${r.totalUnits} unidades · ${r.locations} localizações`)
      setD0Rows([{ productId: '', locationId: '', quantity: 0 }])
    },
    onError: (e: any) => setD0Result(apiErr(e, 'Erro no D0')),
  })

  const setRow = (i: number, k: keyof D0Row, v: string | number) =>
    setD0Rows(p => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r))
  const addRow = () => setD0Rows(p => [...p, { productId: '', locationId: '', quantity: 0 }])
  const removeRow = (i: number) => setD0Rows(p => p.filter((_, idx) => idx !== i))

  const loadOrders = useMutation({
    mutationFn: async () =>
      (await api.get('/integrations/orders/preview', { params: { tenantId, provider: ordProvider, from: ordFrom, to: ordTo } })).data,
    onSuccess: (r: { total: number; orders: OrderRow[] }) => {
      setOrders(r.orders ?? []); setOrdSel(new Set())
      setOrdMsg(`${r.total} pedido(s) na janela`)
    },
    onError: (e: any) => { setOrders([]); setOrdMsg(apiErr(e, 'Erro ao listar pedidos')) },
  })
  const importSel = useMutation({
    mutationFn: async ({ ids, asShipped }: { ids: string[]; asShipped: boolean }) =>
      (await api.post('/integrations/orders/import', { tenantId, provider: ordProvider, externalIds: ids, asShipped })).data,
    onSuccess: (r: { imported: number; failed: number; asShipped?: boolean }) => {
      setOrdMsg(`${r.imported} ${r.asShipped ? 'marcado(s) como já enviado' : 'importado(s)'} · ${r.failed} falha(s)`)
      loadOrders.mutate()
    },
    onError: (e: any) => setOrdMsg(apiErr(e, 'Erro ao importar')),
  })
  const toggleOrd = (id: string) =>
    setOrdSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  // "Aberto" = ainda dá para agir. Fechados (já enviado/faturado/cancelado/
  // devolvido) não entram na seleção. Novos e importados-não-enviados sim.
  const CLOSED_STATUS = new Set(['shipped', 'invoiced', 'cancelled', 'returned'])
  const isOpenOrder = (o: OrderRow) => !o.imported || !CLOSED_STATUS.has(o.localStatus ?? '')
  const novos = (orders ?? []).filter(o => !o.imported)
  const abertos = (orders ?? []).filter(isOpenOrder)

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  const credByProvider = (p: string) => creds.find(c => c.provider === p)
  const d0Valid = d0Rows.some(r => r.productId && r.locationId && r.quantity > 0)

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Integrações</h2>
          <p className="text-sm text-gray-500">Conecte Bling, Olist e Melhor Envio · sincronize produtos · faça o D0</p>
        </div>
        <Button onClick={() => setConnectModal(true)} size="sm"><Plug className="h-4 w-4" /> Conectar</Button>
      </div>

      {/* Abas de organização */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {([['operacao', 'Cargas & sync'], ['canais', 'Canais & regras'], ['conexoes', 'Conexões']] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'operacao' && (<div className="space-y-5">
      {/* Rotina única da esteira: importa pedidos do ERP + casa etiquetas do ME */}
      <div className="flex flex-col gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-semibold text-rose-900">Carregar pedidos + etiquetas</p>
          <p className="text-xs text-rose-700">Um clique: importa os pedidos do ERP e casa as etiquetas do Melhor Envio.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="rounded-lg border border-rose-300 bg-white px-2 py-2 text-sm text-rose-900"
            value={refreshDays}
            onChange={e => setRefreshDays(Number(e.target.value))}
            title="Período de importação"
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={15}>Últimos 15 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={60}>Últimos 60 dias</option>
          </select>
          <Button size="sm" loading={refreshAll.isPending} onClick={() => { setDoneMsg(null); refreshAll.mutate() }}>
            <RefreshCw className="h-4 w-4" /> {refreshAll.isPending ? 'Carregando…' : 'Carregar tudo'}
          </Button>
        </div>
      </div>
      {doneMsg && (
        <div className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${doneMsg.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
          <span className="text-base">{doneMsg.ok ? '✅' : '⚠️'}</span>{doneMsg.text}
        </div>
      )}

      {/* Sync NF-first por mês — pega TikTok/ML e notas atrasadas */}
      <details className="rounded-xl border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-gray-800">Sincronizar por NF (mês) — TikTok/ML e notas atrasadas</summary>
        <div className="mt-3 space-y-2">
          <p className="text-xs text-gray-500">
            Importa <b>pela NF-e emitida no mês</b> (não pela data do pedido). Pega os marketplaces (TikTok/ML), cujo pedido não amarra a NF, e notas atrasadas.
            Já importadas são puladas (idempotente). Roda em segundo plano.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Mês</label>
              <select value={nfSyncMonth} onChange={e => setNfSyncMonth(Number(e.target.value))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Ano</label>
              <input type="number" value={nfSyncYear} onChange={e => setNfSyncYear(Number(e.target.value) || new Date().getFullYear())}
                className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <Button size="sm" loading={nfSync.isPending} onClick={() => { setNfSyncMsg(null); nfSync.mutate() }}>
              <Download className="h-4 w-4" /> Sincronizar por NF
            </Button>
          </div>
          {nfSyncMsg && (
            <div className={`rounded-lg border px-3 py-2 text-sm ${nfSyncMsg.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>{nfSyncMsg.text}</div>
          )}
        </div>
      </details>

      {/* Notas sem data de emissão — o buraco que sumia dos relatórios */}
      {!!semEmissao?.semEmissao && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            {semEmissao.semEmissao.toLocaleString('pt-BR')} nota(s) sem data de emissão
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Elas entraram pela NF (TikTok/ML e notas atrasadas) com número e chave, mas <b>sem a data</b> —
            e todo relatório mensal corta por emissão, então essas notas existem no banco e não aparecem em
            apuração nenhuma. É a diferença entre o que o Bling conta no mês e o que o extrato mostra.
            {semEmissao.semIdDaNota > 0 && (
              <> {semEmissao.semIdDaNota.toLocaleString('pt-BR')} delas não guardaram o id da NF no ERP e
              precisam ser reimportadas pela sincronização por NF acima.</>
            )}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button size="sm" loading={repararEmissao.isPending}
              disabled={!semEmissao.recuperaveis}
              onClick={() => { setReparoMsg(null); repararEmissao.mutate() }}>
              <Download className="h-4 w-4" /> Buscar a emissão no ERP ({semEmissao.recuperaveis.toLocaleString('pt-BR')})
            </Button>
            <span className="text-xs text-amber-700">
              Uma consulta por nota, espaçada pelo limite do Bling — pode levar minutos. Roda em segundo plano.
            </span>
          </div>
          {reparoMsg && (
            <div className={`mt-2 rounded-lg border px-3 py-2 text-sm ${reparoMsg.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>{reparoMsg.text}</div>
          )}
        </div>
      )}

      {/* Contato do destinatário — e-mail e telefone do passado */}
      {!!tenantId && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-sm font-semibold text-blue-900">
            {contatoErro
              ? 'Contato do destinatário — não deu para contar'
              : !semContato
                ? 'Contato do destinatário — conferindo…'
                : semContato.sem_email === 0 && semContato.sem_telefone === 0
                  ? `Contato do destinatário: completo nos ${semContato.total.toLocaleString('pt-BR')} pedidos`
                  : `Contato do destinatário: ${semContato.sem_email.toLocaleString('pt-BR')} sem e-mail · ${semContato.sem_telefone.toLocaleString('pt-BR')} sem telefone (de ${semContato.total.toLocaleString('pt-BR')} pedidos)`}
          </p>
          {!!contatoErro && (
            <p className="mt-1 text-xs text-red-700">
              {(contatoErro as { response?: { data?: { title?: string } } })?.response?.data?.title ?? 'falha ao contar os pedidos sem contato'}
            </p>
          )}
          <p className="mt-1 text-xs text-blue-800">
            O e-mail e o telefone <b>sempre vieram do ERP</b> dentro do endereço do pedido — só não tinham coluna
            própria, então não apareciam em consulta nem na exportação. Por isso o passado se recupera em duas
            etapas: a primeira lê o que já está guardado aqui (instantânea, não consulta o ERP) e a segunda busca
            na NF-e só o que sobrar.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button size="sm" variant="secondary" loading={contatoDaBase.isPending}
              onClick={() => { setContatoMsg(null); contatoDaBase.mutate() }}>
              <Download className="h-4 w-4" /> Preencher pela própria base
            </Button>
            <Button size="sm" loading={contatoDoErp.isPending}
              disabled={!semContato?.buscaveis_no_erp}
              onClick={() => { setContatoMsg(null); contatoDoErp.mutate() }}>
              <Download className="h-4 w-4" /> Buscar o resto no ERP ({(semContato?.buscaveis_no_erp ?? 0).toLocaleString('pt-BR')})
            </Button>
            <span className="text-xs text-blue-700">
              Uma consulta por nota, espaçada pelos 3/s do Bling:{' '}
              {semContato?.buscaveis_no_erp
                ? `~${Math.max(1, Math.round((semContato.buscaveis_no_erp * 0.42) / 60))} min para ${semContato.buscaveis_no_erp.toLocaleString('pt-BR')} nota(s)`
                : 'tempo proporcional à quantidade'}.
              Roda em segundo plano: pode fechar a aba e voltar depois. Se parar no meio (deploy, reinício),
              clique de novo — ele continua de onde estava.
            </span>
          </div>
          {contatoMsg && (
            <div className={`mt-2 rounded-lg border px-3 py-2 text-sm ${contatoMsg.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>{contatoMsg.text}</div>
          )}
        </div>
      )}

      {/* Relatório ORD → NF (Melhor Envio) — identificar ocorrências */}
      <details className="rounded-xl border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-gray-800">Relatório ORD → NF (Melhor Envio) — identificar ocorrências</summary>
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">Cole as <b>ORDs</b> do Melhor Envio (ele só te manda a ORD). O relatório traz a <b>NF fiscal</b>, série, transportadora, rastreio e status de cada uma.</p>
          <textarea value={ordText} onChange={e => setOrdText(e.target.value)} rows={4}
            placeholder="ORD-202608140651073, ORD-…  (ou uma por linha)"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm" />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" loading={ordReport.isPending} onClick={() => ordReport.mutate()}>Gerar relatório</Button>
            {ordResult && <button type="button" onClick={exportOrdCsv} className="text-xs text-blue-600 underline hover:text-blue-800">Exportar CSV</button>}
            {ordResult && <span className="text-xs text-gray-500">{ordResult.filter(r => r.encontrada).length}/{ordResult.length} encontradas</span>}
            {ordResult && (() => {
              const div = ordResult.flatMap(r => r.envios).filter(ordEhDiverg)
              return div.length > 0 ? (
                <>
                  <label className="flex items-center gap-1 text-xs font-semibold text-red-700">
                    <input type="checkbox" className="h-3.5 w-3.5" checked={ordSoDiverg} onChange={e => setOrdSoDiverg(e.target.checked)} />
                    ⚠️ {div.length} divergente(s) — só estes
                  </label>
                  <button type="button"
                    onClick={() => { const nfs = [...new Set(div.map(e => e.nf).filter(Boolean))]; navigator.clipboard?.writeText(nfs.join(' ')); }}
                    className="text-xs text-blue-600 underline hover:text-blue-800">copiar NFs divergentes</button>
                </>
              ) : <span className="text-xs text-emerald-700">✓ sem divergências</span>
            })()}
            <Button size="sm" variant="secondary" loading={ordDeep.isPending} onClick={() => { setOrdDeepMsg(null); ordDeep.mutate() }}>Busca profunda no ME</Button>
            <button type="button" disabled={meDbg.isPending} onClick={() => { setMeDbgOut(null); meDbg.mutate() }}
              className="text-xs text-gray-400 underline hover:text-gray-600 disabled:opacity-50">🔍 Diagnosticar Melhor Envio</button>
          </div>
          {ordDeepMsg && <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">{ordDeepMsg}</div>}
          <p className="text-[11px] text-gray-400">A <b>busca profunda</b> pagina o Melhor Envio para trás até achar as ORDs (inclui as antigas, ex.: maio). Roda em segundo plano — pode levar alguns minutos para ORDs muito antigas.</p>
          {meDbgOut && (
            <pre className="max-h-72 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-[11px] leading-tight text-gray-700">{meDbgOut}</pre>
          )}
          {ordResult && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-1 text-left">ORD</th>
                    <th className="px-2 py-1 text-left">NF</th>
                    <th className="px-2 py-1 text-left">Série</th>
                    <th className="px-2 py-1 text-left">UF</th>
                    <th className="px-2 py-1 text-left">Transportadora</th>
                    <th className="px-2 py-1 text-left">Rastreio</th>
                    <th className="px-2 py-1 text-left">Status envio</th>
                    <th className="px-2 py-1 text-left">Status WsFull</th>
                    <th className="px-2 py-1 text-left">Onda</th>
                    <th className="px-2 py-1 text-left">Pedido</th>
                    <th className="px-2 py-1 text-left">Destinatário</th>
                  </tr>
                </thead>
                <tbody>
                  {ordResult.flatMap(r => r.envios.length
                    ? r.envios.filter(e => !ordSoDiverg || ordEhDiverg(e)).map((e, i) => {
                        const pedLabel: Record<string, string> = {
                          imported: 'a enviar', awaiting_label: 'a enviar', reserved: 'a enviar',
                          picking: 'em separação', packed: 'em separação',
                          shipped: 'expedido', invoiced: 'faturado', cancelled: 'cancelado', returned: 'devolvido', pending: 'pendente',
                        }
                        const divergente = ordEhDiverg(e)
                        const semPedido = !e.pedido
                        return (
                          <tr key={r.ord + i} className={`border-b border-gray-100 ${divergente ? 'bg-red-50' : semPedido ? 'bg-amber-50' : ''}`}>
                            <td className="px-2 py-1 font-mono">{r.ord}</td>
                            <td className="px-2 py-1 font-mono">{e.nf ?? '—'}</td>
                            <td className="px-2 py-1">{e.serie ?? '—'}</td>
                            <td className="px-2 py-1 font-semibold">{e.uf ?? '—'}</td>
                            <td className="px-2 py-1">{e.transportadora ?? '—'}</td>
                            <td className="px-2 py-1 font-mono">{e.rastreio ?? '—'}</td>
                            <td className="px-2 py-1">{e.statusEnvio}</td>
                            <td className={`px-2 py-1 ${divergente ? 'font-semibold text-red-700' : ''}`}>
                              {e.pedido ? (pedLabel[e.statusPedido ?? ''] ?? e.statusPedido ?? '—') : 'não está no WsFull'}
                              {divergente && ' ⚠️'}
                            </td>
                            <td className="px-2 py-1 font-mono text-blue-700">{e.onda?.length ? e.onda.join(', ') : '—'}</td>
                            <td className="px-2 py-1 font-mono">{e.pedido ?? '—'}</td>
                            <td className="px-2 py-1">{e.destinatario ?? '—'}</td>
                          </tr>
                        )
                      })
                    : (ordSoDiverg ? [] : [(
                        <tr key={r.ord} className="border-b border-gray-100 bg-amber-50">
                          <td className="px-2 py-1 font-mono">{r.ord}</td>
                          <td className="px-2 py-1 text-amber-700" colSpan={10}>não encontrada no Melhor Envio (use "Busca profunda no ME" p/ ORDs antigas)</td>
                        </tr>
                      )]))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </details>

      {/* Importar notas específicas por lista de NF */}
      <details className="rounded-xl border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-gray-800">Importar notas específicas (por número de NF)</summary>
        <div className="mt-3 space-y-2">
          <p className="text-xs text-gray-500">Cole os números das NFs (separados por espaço, vírgula ou linha). Ele acha cada nota no ERP, pega o pedido vinculado e importa — útil pra notas antigas que já saíram e não vêm na janela de dias.</p>
          <textarea
            value={nfList}
            onChange={e => setNfList(e.target.value)}
            rows={3}
            placeholder="85409, 85412, 85413&#10;Se houver mais de uma série do mesmo número, use número/série: 48/2"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm"
          />
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="h-4 w-4" checked={nfAsShipped} onChange={e => setNfAsShipped(e.target.checked)} />
              Marcar como <b>já enviado (sem baixa de estoque)</b>
            </label>
            <Button size="sm" loading={importNfs.isPending} onClick={() => { setNfResult(null); importNfs.mutate() }}>
              <Download className="h-4 w-4" /> Buscar e importar NFs
            </Button>
            <button type="button" onClick={() => { setNfDebugOut(null); nfDebug.mutate() }}
              className="text-xs text-gray-400 underline hover:text-gray-600">
              🔍 Diagnosticar 1ª NF
            </button>
            <button type="button" disabled={chanDebug.isPending} onClick={() => { setNfDebugOut(chanDebug.isPending ? null : 'carregando…'); chanDebug.mutate() }}
              className="text-xs text-gray-400 underline hover:text-gray-600 disabled:opacity-50">
              🔍 Diagnosticar canais
            </button>
            <button type="button" disabled={shipReconcile.isPending} onClick={() => { setNfDebugOut('conferindo…'); shipReconcile.mutate(false) }}
              className="text-xs text-gray-400 underline hover:text-gray-600 disabled:opacity-50">
              🔍 Conferir etiquetas (WsFull × ME)
            </button>
            <button type="button" disabled={shipReconcile.isPending}
              onClick={() => { if (window.confirm('Corrigir as etiquetas "presas": sincroniza com o estado real do Melhor Envio (as que sumiram do ME viram Cancelada)?')) { setNfDebugOut('corrigindo…'); shipReconcile.mutate(true) } }}
              className="text-xs text-amber-500 underline hover:text-amber-700 disabled:opacity-50">
              🔧 Corrigir presas
            </button>
          </div>
          {nfResult && (
            <div className={`rounded-lg border px-3 py-2 text-sm ${nfResult.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>{nfResult.text}</div>
          )}
          {nfDebugOut && (
            <pre className="max-h-72 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-[11px] leading-tight text-gray-700">{nfDebugOut}</pre>
          )}
        </div>
      </details>

      {/* Marcar processadas por NF (registro, sem baixa) — notas já expedidas por fora */}
      <MarcarProcessadasNf tenantId={tenantId} />

      {/* Estornar para fracionamento por NF — quando a baixa da onda contou em dobro */}
      <EstornoFracionamento tenantId={tenantId} />

      {/* Fase 0 — Diagnóstico de etiqueta (marketplace via Bling) */}
      <details className="rounded-xl border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-gray-800">Diagnóstico de etiqueta (marketplace / Bling)</summary>
        <div className="mt-3 space-y-2">
          <p className="text-xs text-gray-500">
            Informe o <b>número da NF</b> (mais fácil — resolvo NF → pedido no Bling e mostro <code>linkDanfe</code>/<code>linkPDF</code>) <b>ou</b> o <b>id do pedido no Bling</b>.
            Testa os endpoints de etiqueta (<code>/logisticas/objetos</code> e <code>/logisticas/etiquetas</code>) — é aqui que se vê se o <b>escopo de logística</b> foi liberado.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={labelNf}
              onChange={e => setLabelNf(e.target.value)}
              placeholder="número da NF (ex.: 85812 ou 85812/2)"
              className="w-56 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm"
            />
            <span className="text-xs text-gray-400">ou</span>
            <input
              value={labelOrderId}
              onChange={e => setLabelOrderId(e.target.value)}
              placeholder="id do pedido no Bling"
              className="w-48 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm"
            />
            <Button size="sm" loading={labelDebug.isPending} onClick={() => { setLabelDebugOut(null); labelDebug.mutate() }}>
              🔍 Diagnosticar etiqueta
            </Button>
          </div>
          {labelDebugOut && (
            <pre className="max-h-96 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-[11px] leading-tight text-gray-700">{labelDebugOut}</pre>
          )}
        </div>
      </details>

      </div>)}

      {tab === 'canais' && (<div className="space-y-5">
      {/* Mapa de canais: loja.id (Bling) → nome do canal (Tray/TikTok/Mercado Livre) */}
      <MapaCanais tenantId={tenantId} />

      {/* Destinatários ignorados: notas não-venda (remessa/transferência/indústria) */}
      <DestinatariosIgnorados tenantId={tenantId} />

      {/* Corte e expedição por canal (informativo) */}
      <ScheduleCanais />

      {/* Trava de unicidade por chave da NF (ligar quando as duplicatas zerarem) */}
      <TravaUnicidade />

      </div>)}

      {tab === 'conexoes' && (<div className="space-y-5">
      {/* Backfill do histórico — importa em janelas pequenas (uma vez) */}
      <details className="rounded-xl border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-gray-800">Backfill do histórico (importar tudo desde uma data)</summary>
        <div className="mt-3 space-y-2">
          <p className="text-xs text-gray-500">Importa <b>todos os pedidos</b> do ERP desde a data escolhida até hoje, em janelas no servidor (não estoura timeout). Roda uma vez; depois use o "Carregar tudo" no dia a dia. Retoma de onde parou (mesmo após deploy) — é seguro re-rodar.</p>
          {reconcile && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <b>{reconcile.total.toLocaleString('pt-BR')}</b> pedido(s) no WsFull · {reconcile.comNf.toLocaleString('pt-BR')} com NF · {reconcile.enviados.toLocaleString('pt-BR')} enviado(s)
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-gray-600">Desde:</label>
            <input type="date" className="rounded-lg border border-gray-300 px-2 py-2 text-sm" value={backfillFrom} onChange={e => setBackfillFrom(e.target.value)} disabled={backfilling} />
            <Button size="sm" loading={backfilling} onClick={runBackfill}>
              <Download className="h-4 w-4" /> Importar histórico
            </Button>
          </div>
          {backfillMsg && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">{backfillMsg}</div>
          )}
        </div>
      </details>

      {/* Provedores */}
      <div className="grid gap-4 sm:grid-cols-3">
        {PROVIDERS.map(p => {
          const c = credByProvider(p.value)
          return (
            <Card key={p.value}>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">{p.label}</span>
                  <Badge variant={c?.connected ? 'success' : 'default'}>{c?.connected ? 'Conectado' : 'Não conectado'}</Badge>
                </div>
                <p className="text-xs text-gray-500">
                  {c?.externalAccountId ? `Conta: ${c.externalAccountId}` : 'Sem conta vinculada'}
                  {c?.expiresAt ? ` · expira ${new Date(c.expiresAt).toLocaleString('pt-BR')}` : ''}
                </p>
                {c?.connected && (
                  <p className="text-xs">
                    <span className="text-gray-500">Permissões: </span>
                    {c.scopes
                      ? <span className="text-gray-700">{c.scopes}</span>
                      : <span className="text-amber-600">nenhuma informada pelo provedor</span>}
                    {' · '}
                    {c.canAutoRenew
                      ? <span className="text-green-700">renova sozinho</span>
                      : <span className="text-amber-600">sem refresh_token — vai expirar</span>}
                  </p>
                )}
                {p.isErp && (
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="secondary" size="sm"
                      disabled={!c?.connected}
                      loading={sync.isPending && sync.variables === p.value}
                      onClick={() => { setSyncResult(null); sync.mutate(p.value) }}
                    >
                      <RefreshCw className="h-4 w-4" /> Sincronizar produtos
                    </Button>
                    <Button
                      variant="secondary" size="sm"
                      disabled={!c?.connected}
                      loading={importOrders.isPending && importOrders.variables === p.value}
                      onClick={() => { setSyncResult(null); importOrders.mutate(p.value) }}
                    >
                      <Download className="h-4 w-4" /> Importar pedidos
                    </Button>
                    <Button
                      variant="secondary" size="sm"
                      disabled={!c?.connected}
                      loading={diagnosticar.isPending && diagnosticar.variables === p.value}
                      onClick={() => { setDiag(null); diagnosticar.mutate(p.value) }}
                    >
                      <Stethoscope className="h-4 w-4" /> Por que não entra pedido?
                    </Button>
                    {diag && diag.provider === p.value && (
                      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs">
                        {diag.erro ? (
                          <p className="font-medium text-red-700">O ERP recusou: {diag.erro}</p>
                        ) : (
                          <p className="font-medium text-green-700">Token aceito pelo ERP.</p>
                        )}
                        {diag.janela && (
                          <p className="text-gray-600">
                            Janela: desde {diag.janela.dataInicial} ({diag.janela.dias} dias) ·{' '}
                            {diag.pedidosNaJanela ?? 0} pedido(s) no ERP ·{' '}
                            <b className={diag.prontosParaImportar ? 'text-gray-900' : 'text-amber-700'}>
                              {diag.prontosParaImportar ?? 0} na situação que o WsFull importa
                            </b>
                          </p>
                        )}
                        {diag.regra && <p className="text-gray-500">Regra: {diag.regra}</p>}
                        {!!diag.porSituacao?.length && (
                          <div>
                            <p className="mb-1 font-medium text-gray-700">Situação dos pedidos no ERP:</p>
                            <ul className="space-y-0.5">
                              {diag.porSituacao.map(x => (
                                <li key={x.situacao} className={x.entra ? 'text-green-700' : 'text-gray-500'}>
                                  {x.entra ? '✓' : '—'} {x.situacao}: {x.pedidos}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {diag.noWsFull && (
                          <p className="text-gray-600">
                            No WsFull: {diag.noWsFull.pedidos} pedido(s) deste ERP ·{' '}
                            {diag.noWsFull.ultimoEntrouEm
                              ? `o último entrou em ${new Date(diag.noWsFull.ultimoEntrouEm).toLocaleString('pt-BR')}`
                              : 'nenhum importado ainda'}
                          </p>
                        )}
                        {diag.autoSync?.at && (
                          <p className={diag.autoSync.ok === false ? 'text-red-700' : 'text-gray-600'}>
                            Auto-sync: {new Date(diag.autoSync.at).toLocaleString('pt-BR')} ·{' '}
                            {diag.autoSync.ok === false
                              ? `falhou — ${diag.autoSync.error}`
                              : `${diag.autoSync.imported ?? 0} importado(s) de ${diag.autoSync.total ?? 0}`}
                          </p>
                        )}
                        {!!diag.amostra?.length && (
                          <details>
                            <summary className="cursor-pointer text-gray-600">Últimos pedidos vistos no ERP</summary>
                            <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-gray-600">
                              {diag.amostra.map(a => (
                                <li key={a.id}>
                                  {a.entra ? '✓' : '—'} {a.numero ?? a.id} · {a.situacao} · {a.data ?? '—'}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                    {p.value === 'bling' && (
                      blingCfg && !blingCfg.configured ? (
                        <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-2">
                          <p className="text-xs text-amber-800">Configure o app Bling (v3) uma vez:</p>
                          <input value={blingApp.clientId} onChange={e => setBlingApp(s => ({ ...s, clientId: e.target.value }))}
                            placeholder="Client ID" className="h-8 w-full rounded border border-gray-300 px-2 text-xs" />
                          <input value={blingApp.clientSecret} onChange={e => setBlingApp(s => ({ ...s, clientSecret: e.target.value }))}
                            placeholder="Client Secret" className="h-8 w-full rounded border border-gray-300 px-2 text-xs" />
                          <Button size="sm" className="w-full" loading={saveBlingApp.isPending}
                            disabled={!blingApp.clientId || !blingApp.clientSecret} onClick={() => saveBlingApp.mutate()}>
                            Salvar app Bling
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <Button variant="secondary" size="sm"
                            onClick={() => { window.location.href = `/api/integrations/oauth/bling/start?tenantId=${tenantId}` }}>
                            <Plug className="h-4 w-4" /> {c?.connected ? 'Reconectar' : 'Conectar'} (OAuth)
                          </Button>
                          <button type="button"
                            onClick={() => {
                              const link = `${window.location.origin}/api/integrations/oauth/bling/start?tenantId=${tenantId}`
                              navigator.clipboard?.writeText(link).then(
                                () => alert('Link de autorização copiado. Envie para quem tem acesso ao Bling — ao abrir e autorizar, a conexão é feita SEM precisar de login no WsFull.'),
                                () => window.prompt('Copie o link de autorização e envie para quem tem acesso ao Bling:', link),
                              )
                            }}
                            className="text-xs text-blue-600 underline hover:text-blue-800">
                            Copiar link p/ terceiro autorizar
                          </button>
                        </div>
                      )
                    )}
                  </div>
                )}
                {p.value === 'melhorenvio' && (
                  meCfg && !meCfg.configured ? (
                    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-2">
                      <p className="text-xs text-amber-800">Configure o app Melhor Envio uma vez (Client ID + Secret do seu app):</p>
                      <input value={meApp.clientId} onChange={e => setMeApp(s => ({ ...s, clientId: e.target.value }))}
                        placeholder="Client ID" className="h-8 w-full rounded border border-gray-300 px-2 text-xs" />
                      <input value={meApp.clientSecret} onChange={e => setMeApp(s => ({ ...s, clientSecret: e.target.value }))}
                        placeholder="Client Secret" className="h-8 w-full rounded border border-gray-300 px-2 text-xs" />
                      <Button size="sm" className="w-full" loading={saveMeApp.isPending}
                        disabled={!meApp.clientId || !meApp.clientSecret} onClick={() => saveMeApp.mutate()}>
                        Salvar app Melhor Envio
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="secondary" size="sm"
                      onClick={() => { window.location.href = `/api/integrations/oauth/melhorenvio/start?tenantId=${tenantId}` }}
                    >
                      <Plug className="h-4 w-4" /> {c?.connected ? 'Reconectar' : 'Conectar'} (OAuth)
                    </Button>
                  )
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>
      {syncResult && <p className="text-sm text-gray-600">{syncResult}</p>}

      {/* Pedidos do ERP — preview + importar selecionados */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Download className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-900">Pedidos do {providerLabel(ordProvider)}</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Canal</label>
              <select value={ordProvider} onChange={e => { setOrdProvider(e.target.value as 'bling' | 'olist'); setOrders(null) }}
                className="h-10 rounded-lg border border-gray-300 px-3 text-sm">
                <option value="bling">Bling</option>
                <option value="olist">Olist</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">De</label>
              <input type="date" value={ordFrom} onChange={e => setOrdFrom(e.target.value)}
                className="h-10 rounded-lg border border-gray-300 px-3 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Até</label>
              <input type="date" value={ordTo} onChange={e => setOrdTo(e.target.value)}
                className="h-10 rounded-lg border border-gray-300 px-3 text-sm" />
            </div>
            <Button size="sm" loading={loadOrders.isPending} onClick={() => loadOrders.mutate()}>
              <RefreshCw className="h-4 w-4" /> Listar pedidos
            </Button>
            {novos.length > 0 && (
              <Button size="sm" variant="secondary"
                onClick={() => setOrdSel(new Set(novos.map(o => o.externalId)))}>
                Selecionar {novos.length} novo(s)
              </Button>
            )}
            {abertos.length > 0 && (
              <Button size="sm" variant="secondary"
                onClick={() => setOrdSel(new Set(abertos.map(o => o.externalId)))}>
                Selecionar {abertos.length} aberto(s)
              </Button>
            )}
            {ordSel.size > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setOrdSel(new Set())}>Limpar seleção</Button>
            )}
            {ordSel.size > 0 && (
              <>
                <Button size="sm" loading={importSel.isPending} onClick={() => importSel.mutate({ ids: [...ordSel], asShipped: false })}>
                  <Download className="h-4 w-4" /> Importar {ordSel.size} p/ separar
                </Button>
                <Button size="sm" variant="secondary" loading={importSel.isPending}
                  onClick={() => { if (window.confirm(`Marcar ${ordSel.size} pedido(s) como JÁ ENVIADO, sem baixar estoque? Use para pedidos que saíram por fora do sistema antes da recontagem.`)) importSel.mutate({ ids: [...ordSel], asShipped: true }) }}>
                  Já enviado (sem baixa)
                </Button>
              </>
            )}
          </div>
          <p className="text-xs text-gray-400">
            <b>P/ separar</b>: entra na fila normal (baixa o estoque quando expedir).
            <b> Já enviado (sem baixa)</b>: registra como enviado sem mexer no estoque — para pedidos que saíram por fora do sistema antes da recontagem de hoje.
          </p>

          {ordMsg && <p className="text-sm text-gray-600">{ordMsg}</p>}

          {orders && orders.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-8 px-3 py-2"></th>
                    <th className="px-3 py-2">Pedido</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2">Canal</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                    <th className="px-3 py-2">No sistema</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const st = o.localStatus ? LOCAL_STATUS[o.localStatus] : null
                    return (
                      <tr key={o.externalId} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2">
                          {isOpenOrder(o) && (
                            <input type="checkbox" checked={ordSel.has(o.externalId)} onChange={() => toggleOrd(o.externalId)}
                              className="h-4 w-4 rounded border-gray-300" />
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-gray-700">{o.number ?? o.externalId}</td>
                        <td className="px-3 py-2 text-gray-700">{o.customerName ?? '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{o.channel ?? '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{o.total != null ? o.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</td>
                        <td className="px-3 py-2">
                          {o.imported
                            ? <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${st?.c ?? 'bg-blue-100 text-blue-700'}`}>{st?.t ?? o.localStatus}{o.wsCode ? ` · ${o.wsCode}` : ''}</span>
                            : <span className="rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-semibold text-green-700 ring-1 ring-green-200">Novo</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          {orders && orders.length === 0 && !loadOrders.isPending && (
            <p className="text-sm text-gray-400">Nenhum pedido nessa janela.</p>
          )}
        </CardContent>
      </Card>

      {/* D0 — contagem inicial */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Boxes className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-900">Estoque inicial (D0)</span>
          </div>
          <Button variant="ghost" size="sm" onClick={addRow}><Plus className="h-4 w-4" /> Adicionar linha</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-500">
            Contagem inicial por produto e localização. Cada linha é lançada no estoque (movimento de ajuste).
            Sincronize os produtos antes.
          </p>
          {d0Rows.map((row, i) => (
            <div key={i} className="flex items-center gap-2">
              <select
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={row.productId}
                onChange={e => setRow(i, 'productId', e.target.value)}
              >
                <option value="">Produto...</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
              </select>
              <select
                className="w-40 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={row.locationId}
                onChange={e => setRow(i, 'locationId', e.target.value)}
              >
                <option value="">Local...</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
              </select>
              <input
                type="number" min={0}
                className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm text-right"
                value={row.quantity}
                onChange={e => setRow(i, 'quantity', parseInt(e.target.value) || 0)}
              />
              {d0Rows.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeRow(i)}>✕</Button>}
            </div>
          ))}
          <div className="flex items-center gap-3 pt-1">
            <Button loading={d0.isPending} disabled={!d0Valid} onClick={() => { setD0Result(null); d0.mutate() }}>
              Lançar D0
            </Button>
            {d0Result && <span className="text-sm text-gray-600">{d0Result}</span>}
          </div>
        </CardContent>
      </Card>

      {/* Status detalhado */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Carregando...</p>
          ) : creds.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhuma integração conectada ainda</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Provedor</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Conta</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Atualizado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {creds.map(c => (
                  <tr key={c.provider} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{providerLabel(c.provider)}</td>
                    <td className="px-5 py-3"><Badge variant={c.connected ? 'success' : 'default'}>{c.connected ? 'Conectado' : 'Pendente'}</Badge></td>
                    <td className="px-5 py-3 text-gray-600">{c.externalAccountId ?? '—'}</td>
                    <td className="px-5 py-3 text-gray-500">{new Date(c.updatedAt).toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      </div>)}

      {/* Modal conectar */}
      <Modal title="Conectar integração" open={connectModal} onClose={() => setConnectModal(false)} size="lg">
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Provedor</label>
            <select
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={form.provider}
              onChange={e => setForm({ ...form, provider: e.target.value })}
            >
              {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <Input label="Access token" value={form.accessToken} onChange={e => setForm({ ...form, accessToken: e.target.value })} placeholder="Bearer token" />
          <Input label="Refresh token" value={form.refreshToken} onChange={e => setForm({ ...form, refreshToken: e.target.value })} placeholder="usado para renovar automaticamente" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Conta / companyId" value={form.externalAccountId} onChange={e => setForm({ ...form, externalAccountId: e.target.value })} placeholder="ex: companyId do Bling" />
            <Input label="Expira em" type="datetime-local" value={form.expiresAt} onChange={e => setForm({ ...form, expiresAt: e.target.value })} />
          </div>
          <p className="text-xs text-gray-400">
            Os tokens vêm do fluxo OAuth do provedor. O <b>companyId</b> do Bling é usado para rotear os webhooks ao cliente certo.
          </p>
          {connect.error && <p className="text-sm text-red-600">{(connect.error as any)?.response?.data?.detail ?? 'Erro ao conectar'}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setConnectModal(false)}>Cancelar</Button>
            <Button loading={connect.isPending} onClick={() => connect.mutate()}>Salvar</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// Mapa de canais: loja.id (Bling) → nome do canal (Tray / TikTok / Mercado Livre).
// O /lojas do Bling dá 404, então o canal é identificado pelo id da loja no pedido.
function EstornoFracionamento({ tenantId }: { tenantId: string }) {
  const [nfBox, setNfBox] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  async function estornar() {
    const nfs = [...new Set(nfBox.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean))]
    if (!nfs.length || busy) return
    if (!window.confirm(`Estornar ${nfs.length} NF(s) para o fracionamento? Devolve as unidades desses pedidos ao GERAL. Use SÓ quando a onda JÁ deu baixa e as notas já saíram por fora (baixa em dobro).`)) return
    setBusy(true); setMsg(null)
    try {
      const { data: r } = await api.post('/stock/estorno-fracionamento', { tenantId, nfs })
      setMsg(`Estornado: ${r.unidades} unidade(s) em ${r.skus} SKU(s) de ${r.orders} pedido(s).${r.notFound?.length ? ` · não achei: ${r.notFound.join(', ')}` : ''}`)
      setNfBox('')
    } catch (e: any) { setMsg(e?.response?.data?.title ?? 'Falha ao estornar') }
    finally { setBusy(false) }
  }

  return (
    <details className="rounded-xl border border-gray-200 bg-white p-4">
      <summary className="cursor-pointer text-sm font-semibold text-gray-800">Estornar para fracionamento por NF (nota já expedida)</summary>
      <div className="mt-3 space-y-2">
        <p className="text-xs text-gray-500">
          Devolve ao <b>fracionamento (GERAL)</b> as unidades dos pedidos dessas NFs. Use quando a onda <b>já deu baixa</b> mas as notas <b>já tinham saído</b> por fora — a baixa contou em dobro. Cria um lançamento de estorno (fica no log de Movimentações). Cole os números das NFs (espaço, vírgula ou linha).
        </p>
        <textarea value={nfBox} onChange={e => setNfBox(e.target.value)} rows={3}
          placeholder="ex.: 83145  82703  82907  83099  82875"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm" />
        <Button size="sm" loading={busy} disabled={!nfBox.trim()} onClick={estornar}>Estornar para fracionamento</Button>
        {msg && <p className="text-xs text-gray-600">{msg}</p>}
      </div>
    </details>
  )
}

function MarcarProcessadasNf({ tenantId }: { tenantId: string }) {
  const [nfBox, setNfBox] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  // Dedup + envia em blocos (200) p/ não estourar o timeout de 60s do servidor.
  async function marcar() {
    const todas = nfBox.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
    const nfs = [...new Set(todas)]
    if (!nfs.length || busy) return
    if (!window.confirm(`Marcar ${nfs.length} nota(s) única(s) como ENVIADO em modo REGISTRO (sem baixar estoque)? Casa pelo número da NF.${todas.length !== nfs.length ? ` (${todas.length - nfs.length} duplicada(s) ignorada(s))` : ''}`)) return
    setBusy(true); setMsg(null)
    const CH = 200
    let shipped = 0, already = 0, notFound = 0
    const notFoundNfs: string[] = []
    try {
      for (let i = 0; i < nfs.length; i += CH) {
        const bloco = nfs.slice(i, i + CH)
        setMsg(`Processando ${Math.min(i + CH, nfs.length)}/${nfs.length}…`)
        const { data: r } = await api.post('/expedition/ship-nfs', { tenantId, nfs: bloco })
        shipped += r.shipped ?? 0; already += r.already ?? 0; notFound += r.notFound ?? 0
        if (Array.isArray(r.notFoundNfs)) notFoundNfs.push(...r.notFoundNfs)
      }
      const nao = notFoundNfs.length ? ` · não achei ${notFound}: ${notFoundNfs.slice(0, 15).join(', ')}${notFoundNfs.length > 15 ? '…' : ''}` : ''
      setMsg(`${shipped} marcada(s) como enviada (registro)${already ? ` · ${already} já estavam` : ''}${notFound ? ` · ${notFound} não encontrada(s)` : ''}.${nao}`)
      setNfBox('')
    } catch (e: any) {
      setMsg(`Parou em ${shipped + already} processada(s): ${e?.response?.data?.title ?? 'falha ao marcar por NF'}`)
    } finally { setBusy(false) }
  }

  return (
    <details className="rounded-xl border border-gray-200 bg-white p-4">
      <summary className="cursor-pointer text-sm font-semibold text-gray-800">Marcar processadas por NF (registro, sem baixa)</summary>
      <div className="mt-3 space-y-2">
        <p className="text-xs text-gray-500">Cole os números das NFs já expedidas por fora (o arquivo de notas processadas) — separados por espaço, vírgula ou linha. Casa pelo número da NF e marca como <b>enviado</b> sem baixar estoque. Aceita <b>número</b> ou <b>número/série</b>.</p>
        <textarea value={nfBox} onChange={e => setNfBox(e.target.value)} rows={3}
          placeholder="ex.: 85580  85581  48/2"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
        <Button size="sm" loading={busy} disabled={!nfBox.trim()} onClick={marcar}>Marcar como enviado (registro)</Button>
        {msg && <p className="text-xs text-gray-600">{msg}</p>}
      </div>
    </details>
  )
}

function DestinatariosIgnorados({ tenantId }: { tenantId: string }) {
  const [docs, setDocs] = useState('')
  const [names, setNames] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!tenantId) return
    api.get('/integrations/ignored-recipients', { params: { tenantId } })
      .then(({ data }) => { setDocs((data?.docs ?? []).join('\n')); setNames((data?.names ?? []).join('\n')) })
      .catch(() => { /* vazio */ })
  }, [tenantId])

  const parse = (s: string) => s.split(/[\n,;]+/).map(x => x.trim()).filter(Boolean)

  async function salvar() {
    setBusy('save'); setMsg(null)
    try {
      const { data } = await api.post('/integrations/ignored-recipients', { tenantId, docs: parse(docs), names: parse(names) })
      setDocs((data?.docs ?? []).join('\n')); setNames((data?.names ?? []).join('\n'))
      setMsg(`Salvo: ${data?.docs?.length ?? 0} CNPJ(s) e ${data?.names?.length ?? 0} nome(s). A partir de agora essas notas não entram na fila.`)
    } catch (e: any) { setMsg(e?.response?.data?.title ?? 'Falha ao salvar') }
    finally { setBusy(null) }
  }

  async function limpar() {
    if (!window.confirm('Tirar da fila (cancelar, sem mexer em estoque) os pedidos cujo destinatário está nesta lista? Salve a lista antes.')) return
    setBusy('clean'); setMsg(null)
    try {
      const { data } = await api.post('/integrations/ignored-recipients/cleanup', { tenantId })
      setMsg(`${data?.cancelled ?? 0} pedido(s) tirado(s) da fila.`)
    } catch (e: any) { setMsg(e?.response?.data?.title ?? 'Falha ao limpar') }
    finally { setBusy(null) }
  }

  return (
    <details className="rounded-xl border border-gray-200 bg-white p-4">
      <summary className="cursor-pointer text-sm font-semibold text-gray-800">Destinatários ignorados (notas que não são venda)</summary>
      <div className="mt-3 space-y-3 text-sm">
        <p className="text-xs text-gray-500">
          Notas de <b>remessa / transferência / industrialização</b> (destinatário = a própria indústria, laboratório etc.) não são venda e <b>não devem ir para a expedição</b>. Liste o <b>CNPJ</b> e/ou o <b>nome</b> desses destinatários: elas deixam de entrar na sincronização por NF. Use "Limpar da fila agora" para tirar as que já entraram.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">CNPJs (um por linha)</label>
            <textarea value={docs} onChange={e => setDocs(e.target.value)} rows={4}
              placeholder="12.345.678/0001-90&#10;98765432000155"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Nomes (contém — um por linha)</label>
            <textarea value={names} onChange={e => setNames(e.target.value)} rows={4}
              placeholder="WS INDÚSTRIA&#10;TOCANE"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs" />
          </div>
        </div>
        <p className="text-[11px] text-gray-400">Nome casa por <b>trecho</b> (ex.: "WS INDÚSTRIA" pega "WS INDÚSTRIA E COMÉRCIO…"). CNPJ casa exato (pontuação é ignorada).</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" loading={busy === 'save'} onClick={salvar}>Salvar lista</Button>
          <Button size="sm" variant="secondary" loading={busy === 'clean'} onClick={limpar}>Limpar da fila agora</Button>
        </div>
        {msg && <p className="text-xs text-gray-600">{msg}</p>}
      </div>
    </details>
  )
}

function TravaUnicidade() {
  const qc = useQueryClient()
  const { data } = useQuery<{ active: boolean; duplicateGroups: number }>({
    queryKey: ['nfkey-unique-status'],
    queryFn: async () => (await api.get('/integrations/nfkey-unique/status')).data,
  })
  const [msg, setMsg] = useState<string | null>(null)
  const enable = useMutation({
    mutationFn: async () => (await api.post('/integrations/nfkey-unique/enable')).data,
    onSuccess: () => { setMsg('Trava ativada — a mesma NF não vira mais dois pedidos.'); qc.invalidateQueries({ queryKey: ['nfkey-unique-status'] }) },
    onError: (e: any) => setMsg(e?.response?.data?.title ?? 'Falha ao ativar'),
  })
  const resolve = useMutation({
    mutationFn: async () => (await api.post('/integrations/nfkey-unique/resolve-duplicates')).data as { groups: number; cancelled: number; needsManual: number },
    onSuccess: (r) => {
      setMsg(`${r.cancelled} pedido(s) duplicado(s) cancelado(s) em ${r.groups} NF(s).${r.needsManual ? ` ${r.needsManual} precisam de revisão manual (já expedidas 2×).` : ' Pode ativar a trava.'}`)
      qc.invalidateQueries({ queryKey: ['nfkey-unique-status'] })
    },
    onError: (e: any) => setMsg(e?.response?.data?.title ?? 'Falha ao resolver duplicatas'),
  })
  const dups = data?.duplicateGroups ?? 0
  return (
    <details className="rounded-xl border border-gray-200 bg-white p-4">
      <summary className="cursor-pointer text-sm font-semibold text-gray-800">Trava de unicidade por NF (chave)</summary>
      <div className="mt-3 space-y-2 text-sm">
        <p className="text-xs text-gray-500">Impede que a mesma NF (pela chave) vire dois pedidos. Só ativa quando não houver duplicatas — clique quando estiver tudo limpo.</p>
        <div className="flex flex-wrap items-center gap-3">
          <span>Status: {data?.active ? <b className="text-emerald-700">ativa ✓</b> : <b className="text-gray-500">inativa</b>}</span>
          {!data?.active && <span>NFs duplicadas: <b className={dups > 0 ? 'text-amber-700' : 'text-emerald-700'}>{data ? dups : '—'}</b></span>}
          {!data?.active && dups > 0 && (
            <Button size="sm" variant="secondary" loading={resolve.isPending}
              onClick={() => { if (window.confirm(`Cancelar automaticamente o pedido extra de cada NF duplicada (mantém o mais avançado, sem mexer em estoque)?`)) { setMsg(null); resolve.mutate() } }}>
              Resolver duplicatas
            </Button>
          )}
          {!data?.active && (
            <Button size="sm" loading={enable.isPending} disabled={!data || dups > 0}
              onClick={() => { setMsg(null); enable.mutate() }}>Ativar trava</Button>
          )}
        </div>
        {!data?.active && dups > 0 && <p className="text-xs text-amber-700">Há {dups} NF(s) com pedido duplicado (mesma chave). Clique em <b>"Resolver duplicatas"</b> para cancelar os extras e liberar a trava.</p>}
        {msg && <p className="text-xs text-gray-600">{msg}</p>}
      </div>
    </details>
  )
}

function ScheduleCanais() {
  type Row = { canal: string; cutoff: string; maxExpedition: string }
  const [rows, setRows] = useState<Row[]>([{ canal: '', cutoff: '', maxExpedition: '' }])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => {
    api.get('/integrations/channel-schedule').then(({ data }) => {
      const rs = Object.entries(data ?? {}).map(([canal, v]: [string, any]) => ({ canal, cutoff: v?.cutoff ?? '', maxExpedition: v?.maxExpedition ?? '' }))
      if (rs.length) setRows(rs)
    }).catch(() => { /* vazio */ })
  }, [])

  const set = (i: number, patch: Partial<Row>) => setRows(r => r.map((x, idx) => idx === i ? { ...x, ...patch } : x))
  const add = () => setRows(r => [...r, { canal: '', cutoff: '', maxExpedition: '' }])
  const del = (i: number) => setRows(r => r.filter((_, idx) => idx !== i))

  async function salvar() {
    setBusy(true); setMsg(null)
    try {
      const schedule: Record<string, { cutoff: string; maxExpedition: string }> = {}
      for (const r of rows) { const c = r.canal.trim(); if (c) schedule[c] = { cutoff: r.cutoff.trim(), maxExpedition: r.maxExpedition.trim() } }
      await api.post('/integrations/channel-schedule', { schedule })
      setMsg(`Salvo: ${Object.keys(schedule).length} canal(is).`)
    } catch (e: any) { setMsg(e?.response?.data?.title ?? 'Falha ao salvar') }
    finally { setBusy(false) }
  }

  return (
    <details className="rounded-xl border border-gray-200 bg-white p-4">
      <summary className="cursor-pointer text-sm font-semibold text-gray-800">Corte e expedição por canal (informativo)</summary>
      <div className="mt-3 space-y-2">
        <p className="text-xs text-gray-500">Horário de <b>corte</b> e <b>prazo máximo de expedição</b> de cada canal. Por enquanto é <b>informativo</b> — para planejar as ondas por canal.</p>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <input value={r.canal} onChange={e => set(i, { canal: e.target.value })} placeholder="Canal (ex.: Mercado Livre)" className="w-48 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <input value={r.cutoff} onChange={e => set(i, { cutoff: e.target.value })} placeholder="Corte (ex.: 14:00)" className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <input value={r.maxExpedition} onChange={e => set(i, { maxExpedition: e.target.value })} placeholder="Expedição máx. (ex.: mesmo dia / D+1)" className="w-60 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <button onClick={() => del(i)} className="rounded-lg px-2 py-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Remover">✕</button>
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={add}><Plus className="h-4 w-4" /> Adicionar canal</Button>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" loading={busy} onClick={salvar}>Salvar</Button>
          {msg && <span className="text-xs text-gray-600">{msg}</span>}
        </div>
      </div>
    </details>
  )
}

function MapaCanais({ tenantId }: { tenantId: string }) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => {
    api.get('/integrations/channel-map').then(({ data }) => {
      const lines = Object.entries(data ?? {}).map(([id, name]) => `${id}: ${name}`)
      setText(lines.join('\n'))
    }).catch(() => { /* vazio */ })
  }, [])

  function parse(): Record<string, string> {
    const map: Record<string, string> = {}
    for (const line of text.split('\n')) {
      const m = line.match(/^\s*(\d+)\s*[:=]\s*(.+?)\s*$/)
      if (m) map[m[1]] = m[2]
    }
    return map
  }

  async function salvar() {
    setBusy(true); setMsg(null)
    try {
      const map = parse()
      await api.post('/integrations/channel-map', { map })
      setMsg(`Mapa salvo: ${Object.keys(map).length} canal(is).`)
    } catch (e: any) { setMsg(e?.response?.data?.title ?? 'Falha ao salvar') }
    finally { setBusy(false) }
  }

  async function aplicar() {
    if (!window.confirm('Aplicar o mapa aos pedidos? Preenche o canal pelos que já têm o loja.id e re-busca no ERP os da fila que ainda não têm (pode levar um tempo).')) return
    setBusy(true); setMsg(null)
    try {
      await salvar()
      const { data: r } = await api.post('/integrations/channel-map/apply', { tenantId, provider: 'bling' })
      setMsg(`${r.updated} pedido(s) com canal atualizado · ${r.refetched} re-buscado(s) no ERP.`)
    } catch (e: any) { setMsg(e?.response?.data?.title ?? 'Falha ao aplicar') }
    finally { setBusy(false) }
  }

  return (
    <details className="rounded-xl border border-gray-200 bg-white p-4">
      <summary className="cursor-pointer text-sm font-semibold text-gray-800">Mapa de canais (loja.id → Tray / TikTok / Mercado Livre)</summary>
      <div className="mt-3 space-y-2">
        <p className="text-xs text-gray-500">
          Um canal por linha, no formato <b>loja.id: Nome</b> (ex.: <code>206022881: Tray</code>). Os ids aparecem no
          <b> "Diagnosticar canais"</b> em <code>lojas_distintas_para_mapear</code>. Use <b>Tray</b>, <b>TikTok</b>,
          <b> Mercado Livre</b> (o nome define o ícone).
        </p>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={4}
          placeholder={'206022881: Tray\n123456: TikTok\n789012: Mercado Livre'}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs focus:border-blue-500 focus:outline-none" />
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" loading={busy} onClick={salvar}>Salvar mapa</Button>
          <Button size="sm" loading={busy} onClick={aplicar}>Salvar e aplicar aos pedidos</Button>
        </div>
        {msg && <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{msg}</div>}
      </div>
    </details>
  )
}
